import { Component, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  fallback?: ReactNode;
  children: ReactNode;
  i18n?: {
    title: string;
    body: string;
    retry: string;
  };
}
interface State {
  hasError: boolean;
  error: Error | null;
}

// Wraps chart-heavy areas. The most common crash on this codebase is
// "Failed to execute 'removeChild' on 'Node'": Recharts' ResponsiveContainer
// trips when external scripts (browser extensions, Cloudflare beacons) mutate
// DOM that React owns. We swallow it so the surrounding page stays usable.
class ErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('ErrorBoundary caught:', error);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    const i18n = this.props.i18n ?? {
      title: 'An error occurred while rendering',
      body: 'A browser extension may be conflicting with page rendering. Reloading often resolves this.',
      retry: 'Retry',
    };
    return (
      <div className="p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-800 dark:text-amber-200">
        <p className="font-semibold mb-1">{i18n.title}</p>
        <p className="text-sm mb-3">{i18n.body}</p>
        <button onClick={this.reset}
          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded text-sm font-semibold">
          {i18n.retry}
        </button>
      </div>
    );
  }
}

export default function ErrorBoundary({ fallback, children }: { fallback?: ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <ErrorBoundaryInner
      fallback={fallback}
      i18n={{
        title: t('errors.boundary.title'),
        body: t('errors.boundary.body'),
        retry: t('errors.boundary.retry'),
      }}
    >
      {children}
    </ErrorBoundaryInner>
  );
}
